import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PublicApiAllowedOperation, PublicApiKeyItem } from '@provision/entity-types';
import { Controller, useForm } from 'react-hook-form';
import { useCreatePublicApiKey, useUpdatePublicApiKey } from 'src/graphql';
import { PermissionTable } from 'src/pages/Settings/integrations/developer/permission-table';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';

export type PublicApiKeyModalProps =
  | {
      mode: 'create';
    }
  | {
      mode: 'edit';
      apiKey: PublicApiKeyItem;
      onUpdated?: (result: PublicApiKeyItem) => void;
    };

interface FormValues {
  name: string;
  allowedOperations: PublicApiAllowedOperation[];
}

const EMPTY_FORM_VALUES: FormValues = {
  name: '',
  allowedOperations: [],
};

export const PublicApiKeyModal = () => {
  const { closeModal } = useModalMethods(Modals.PublicApiKey);
  const { isOpened, modalProps } = useModalState(Modals.PublicApiKey);
  const { createPublicApiKey, isCreating } = useCreatePublicApiKey();
  const { updatePublicApiKey, isUpdating } = useUpdatePublicApiKey();

  const isEdit = modalProps?.mode === 'edit';
  const isBusy = isCreating || isUpdating;
  const initialValues: FormValues =
    modalProps?.mode === 'edit'
      ? {
          name: modalProps.apiKey.name,
          allowedOperations: modalProps.apiKey.allowedOperations,
        }
      : EMPTY_FORM_VALUES;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  if (!modalProps) return null;

  const handleClose = () => {
    if (isBusy) return;
    reset(EMPTY_FORM_VALUES);
    closeModal();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (modalProps.mode === 'create') {
      const trimmedName = values.name.trim();
      const result = await createPublicApiKey({
        name: trimmedName,
        allowedOperations: values.allowedOperations,
      });
      if (result) {
        reset(EMPTY_FORM_VALUES);
        closeModal();
      }
      return;
    }

    const result = await updatePublicApiKey({
      apiKeyId: modalProps.apiKey.apiKeyId,
      allowedOperations: values.allowedOperations,
    });
    if (result) {
      modalProps.onUpdated?.(result);
      closeModal();
    }
  });

  return (
    <Dialog open={isOpened} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={onSubmit} noValidate>
        <DialogTitle>
          <Stack gap={0.5}>
            <Typography variant="h6">
              {isEdit ? 'Edit API key scopes' : 'Create API key'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit
                ? 'Update the permissions for this key. The secret is not changed.'
                : 'Name the key and choose permissions. The secret will be available from the keys list.'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack gap={2.5} pt={1}>
            <Stack gap={0.5}>
              <Controller
                name="name"
                control={control}
                rules={
                  isEdit
                    ? undefined
                    : {
                        validate: (value) => value.trim().length > 0 || 'Key name is required',
                      }
                }
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Key name"
                    size="small"
                    autoFocus={!isEdit}
                    disabled={isBusy || isEdit}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Stack>
            <Stack gap={0.5}>
              <Typography variant="body2">Permissions</Typography>
              <Controller
                name="allowedOperations"
                control={control}
                rules={{
                  validate: (value) => value.length > 0 || 'Select at least one permission',
                }}
                render={({ field }) => (
                  <PermissionTable initialOperations={field.value} onChange={field.onChange} />
                )}
              />
              {errors.allowedOperations ? (
                <Typography variant="caption" color="error">
                  {errors.allowedOperations.message}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isBusy || !isValid}>
            {isEdit ? 'Save changes' : 'Create key'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
